import React, { useEffect, useState } from "react";
import { FieldSelect } from "../FieldPrimitives";
import { PracticeId, practices } from "../practices";
import "./encounter-workbench.css";
import { PRACTICE_STATUS_WORDS, clinicalApi } from "./clinicalApi";
import type { PracticeAccess, PracticeKey, PracticeStatus } from "./types";

/**
 * The practice switcher. Availability is the server's (GET /clinical/practices?facilityId=): which practices this
 * provider may use at this building, said in words. While the answer is loading, or could not be had, Audiology stays
 * the only usable choice and Podiatry says its status is unavailable: it is never enabled by default. Choosing a
 * practice changes the in-memory choice only; no route and no URL changes.
 */
export type PracticesLoad =
  | { state: "loading" }
  | { state: "ready"; byKey: Partial<Record<PracticeKey, PracticeAccess>> }
  | { state: "unavailable" };

const KNOWN: PracticeStatus[] = ["ACTIVE", "NOT_AUTHORIZED", "NOT_AT_FACILITY", "NOT_OPEN", "NOT_AVAILABLE"];

/** The practices answer for one facility. A malformed or refused answer is "unavailable", never "available". */
export function usePractices(facilityId: string): PracticesLoad {
  const [load, setLoad] = useState<{ facilityId: string; result: PracticesLoad } | null>(null);
  useEffect(() => {
    if (!facilityId) return;
    let live = true;
    (async () => {
      let result: PracticesLoad;
      try {
        const r = await clinicalApi.practices(facilityId);
        const list = r.success && r.data && Array.isArray(r.data.practices) ? r.data.practices : null;
        if (list) {
          const byKey: Partial<Record<PracticeKey, PracticeAccess>> = {};
          list.forEach((p) => {
            if (p && typeof p.key === "string" && KNOWN.includes(p.status)) byKey[p.key] = p;
          });
          result = { state: "ready", byKey };
        } else {
          result = { state: "unavailable" };
        }
      } catch {
        result = { state: "unavailable" };
      }
      if (live) setLoad({ facilityId, result });
    })();
    return () => {
      live = false;
    };
  }, [facilityId]);
  // An answer for another building is not an answer for this one.
  if (!facilityId) return { state: "unavailable" };
  return load && load.facilityId === facilityId ? load.result : { state: "loading" };
}

/** The status of one practice, as the switcher shows it (null = the server said nothing usable). */
export function practiceStatusOf(load: PracticesLoad, key: PracticeKey): PracticeStatus | null {
  return load.state === "ready" ? (load.byKey[key]?.status ?? null) : null;
}

export function podiatryOpenIn(load: PracticesLoad): boolean {
  return practiceStatusOf(load, "PODIATRY") === "ACTIVE";
}

const upper = (id: PracticeId) => id.toUpperCase() as PracticeKey;

/** The words after the practice name. Audiology is the existing workspace and is always usable. */
function optionWords(id: PracticeId, load: PracticesLoad): { words: string; usable: boolean } {
  if (id === "audiology") return { words: "", usable: true };
  if (load.state === "loading") return { words: "checking access…", usable: false };
  if (load.state === "unavailable") return { words: "status unavailable", usable: false };
  const status = practiceStatusOf(load, upper(id));
  if (status === null) return { words: "status unavailable", usable: false };
  if (status === "ACTIVE") return { words: "", usable: true };
  return { words: PRACTICE_STATUS_WORDS[status], usable: false };
}

export interface PracticeSwitcherProps {
  facilityId: string;
  value: PracticeId;
  onChange: (practice: PracticeId) => void;
}

export function PracticeSwitcher({ facilityId, value, onChange }: PracticeSwitcherProps) {
  const load = usePractices(facilityId);
  const options = (Object.keys(practices) as PracticeId[]).map((id) => {
    const { words, usable } = optionWords(id, load);
    return { value: id, label: `${practices[id].name}${words ? ` · ${words}` : ""}`, disabled: !usable, words };
  });

  // A practice that is not confirmed usable for THIS building is never left selected (a building change, a lost
  // answer): fall back to Audiology, whose behaviour is unchanged.
  const selected = options.find((o) => o.value === value);
  const selectedUsable = !!selected && !selected.disabled;
  useEffect(() => {
    if (!selectedUsable && value !== "audiology") onChange("audiology");
  }, [selectedUsable, value, onChange]);

  const Icon = practices[selectedUsable ? value : "audiology"].Icon;
  const podiatry = options.find((o) => o.value === "podiatry");
  return (
    <>
      <div className="fw-practice-select">
        <Icon />
        <FieldSelect
          label="Practice module"
          value={selectedUsable ? value : "audiology"}
          onChange={(v) => {
            if (v in practices && options.find((o) => o.value === v && !o.disabled)) onChange(v as PracticeId);
          }}
          options={options.map(({ value: v, label, disabled }) => ({ value: v, label, disabled }))}
        />
      </div>
      {podiatry && podiatry.words && (
        <p className="fw-practice-note" data-testid="practice-note">
          Podiatry: {podiatry.words}
        </p>
      )}
    </>
  );
}

export default PracticeSwitcher;
